/**
 * Subs' progress waiting on the PM, at the top of the Schedule tab (Duncan 2026-10-28, as his mockup; his picks: the PM
 * confirms it, and a lower % shows too, "PM decides"): a sub's task whose progress on E3 Subs -- its fixtures installed,
 * or a Documents task's slider -- isn't the schedule's. The server works out what waits (subProgress.js); the schedule's
 * own % is read live here, so an edit in the task list shows at once.
 *
 * Accept sets the task's % as the PM's own edit does (updateTask, its status from the %), so SOV and billing follow --
 * as much of it as the task's dates allow by the billing date (or today), as the schedule caps every %. Each held-back row
 * says why in plain words (Duncan 2026-10-28: "Really think about a good way to explain this to a user"): billing follows
 * the schedule, so a sub ahead of it needs the task's finish (or, not started yet, its start) changed -- "Change finish
 * date…" opens the task there. "Not now" puts it off until the sub's % changes again (a bell dismissal of that %).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '../ui';
import { ErrorBanner, PrimaryButton, SecondaryButton } from './SubUi';
import ConfirmDialog from '../ConfirmDialog';
import useConfirm from '../../hooks/useConfirm';
import { dismissNotification, updateTask } from '../../api';

const muted = { fontSize: 13, color: 'var(--text-3)' };
const smallBtn = { height: 30, fontSize: 13, padding: '0 12px' };
const statusOf = (pct) => (pct >= 100 ? 'Complete' : pct > 0 ? 'In Progress' : 'Not Started');
/** "Oct 31" ("Mar 5, 2027" in another year) from "2026-10-31", as that day here. */
export const dayOf = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
  if (!m) return '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString('en-US', d.getFullYear() === new Date().getFullYear()
    ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' });
};

/** "124 of 200 fixtures installed", or "Documents slider": what a sub's % is from. */
export const basisOf = (x) => (x.basis && x.basis.documents ? 'Documents slider'
  : x.basis && x.basis.fixtures ? `${Number(x.basis.installed).toLocaleString()} of ${Number(x.basis.fixtures).toLocaleString()} fixtures installed` : null);
/** A sub's % in words, for the Gantt's tag and the list's pill: "Test Lighting Sub says 62% (124 of 200 …). Waiting on you." */
export const saidOf = (x) => `${x.sub_name} says ${x.sub_pct}%${basisOf(x) ? ` (${basisOf(x)})` : ''}. Waiting on you.`;
/** "Oct 31, the billing date" or "today, Oct 28": the day a task's % is capped by. */
export const byOf = (x) => (x.ref_is_draft ? `${dayOf(x.ref_day)}, the billing date` : `today, ${dayOf(x.ref_day)}`);
/**
 * The most % a task's dates allow by `refDay`: routes/tasks.js's cap on every save (server/subProgress.js maxPctOf
 * has the same math) -- for Task Properties, as its dates change.
 */
export const maxPctOf = (start, finish, refDay) => {
  const s = /^(\d{4}-\d{2}-\d{2})/.exec(String(start || ''));
  const f = /^(\d{4}-\d{2}-\d{2})/.exec(String(finish || ''));
  if (!s || !f || !refDay) return 100;
  const ref = new Date(`${refDay}T23:59:59`);
  const taskStart = new Date(`${s[1]}T00:00:00`);
  const taskEnd = new Date(`${f[1]}T23:59:59`);
  const dayMs = 24 * 60 * 60 * 1000;
  const total = Math.max(1, Math.round((taskEnd - taskStart) / dayMs) + 1);
  if (ref < taskStart) return 0;
  if (ref >= taskEnd) return 100;
  return Math.min(100, Math.ceil((Math.max(1, Math.round((ref - taskStart) / dayMs) + 1) / total) * 100));
};
/** The latest finish that lets `pct` be billed by `refDay` (null when the task hasn't started by then): the finish the
 * note in Task Properties suggests -- an earlier one allows more. */
export const finishForPct = (start, refDay, pct) => {
  const s = /^(\d{4}-\d{2}-\d{2})/.exec(String(start || ''));
  if (!s || !refDay || refDay < s[1]) return null;
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const d = new Date(`${refDay}T12:00:00`);
  let best = null;
  for (let i = 0; i < 2000 && maxPctOf(start, ymd(d), refDay) >= pct; i += 1) {
    best = ymd(d);
    d.setDate(d.getDate() + 1);
  }
  return best;
};

/** The item with the schedule's % as it stands now (`live`), or null once the schedule has the sub's %. */
export const withLive = (x, live) => {
  const schedule = live == null ? x.schedule_pct : live;
  if (schedule === x.sub_pct) return null;
  const target = Math.min(x.sub_pct, x.max_pct);
  return { ...x, schedule_pct: schedule, target_pct: target, can_accept: x.sub_pct > schedule ? target > schedule : target < schedule };
};

function ProgressRow({ item, code, focused, busy, onAccept, onNotNow, onOpenTask }) {
  const ref = useRef(null);
  useEffect(() => {
    if (focused && ref.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focused]);
  const lower = item.sub_pct < item.schedule_pct;
  const basis = basisOf(item);
  // Why it can't all be taken, in plain words (as Duncan's mockup): billing follows the schedule.
  const notStarted = item.capped && item.max_pct === 0 && !lower;
  const ahead = item.capped && item.max_pct > 0 && !lower;
  const by = item.ref_is_draft ? `${dayOf(item.ref_day)} (the billing date)` : `today (${dayOf(item.ref_day)})`;
  const why = notStarted
    ? ['Not started on the schedule.', ` It isn’t due to start until ${dayOf(item.start_date)}, so nothing can be billed for it yet. Started early? Change its start date.`]
    : ahead
      ? ['Ahead of the schedule.', ` The schedule has this running to ${dayOf(item.finish_date)}, so by ${by} it can bill ${item.max_pct}%. Finished early? Change its finish date.`]
      : lower
        ? ['Behind the schedule.', ' The sub reports less than the schedule shows. Accept to match it, or keep yours.']
        : null;
  return (
    <div ref={ref} style={{
      display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 10, alignItems: 'center', padding: '10px 14px',
      borderTop: '1px solid var(--border-subtle)', background: focused ? 'var(--warning-soft)' : undefined,
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, color: 'var(--text-1)', fontWeight: 500 }}>
          <span className="mono" style={{ color: 'var(--text-3)', fontSize: 12.5, marginRight: 6 }}>{code || item.task_code}</span>
          {item.task_name}
          {item.place && <span style={{ color: 'var(--text-3)', fontWeight: 400 }}> · {item.place}</span>}
        </div>
        <div style={{ ...muted, marginTop: 2 }}>{[item.sub_name, basis].filter(Boolean).join(' · ')}</div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13.5, marginTop: 4, flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--text-3)', textDecoration: 'line-through' }}>Schedule {item.schedule_pct}%</span>
          <span aria-hidden="true" style={{ color: 'var(--text-3)' }}>→</span>
          <span style={{ fontWeight: 600, color: lower ? 'var(--warning)' : 'var(--success)' }}>Sub says {item.sub_pct}%</span>
        </div>
        {why && (
          <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 5, lineHeight: 1.45 }}>
            <b style={{ fontWeight: 600, color: 'var(--text-1)' }}>{why[0]}</b>{why[1]}
          </div>
        )}
      </div>
      <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {item.can_accept && (
          <PrimaryButton busy={busy} onClick={onAccept} style={smallBtn}>{ahead ? `Take ${item.target_pct}% now` : `Accept ${item.target_pct}%`}</PrimaryButton>
        )}
        {(ahead || notStarted) && (
          <SecondaryButton onClick={onOpenTask} style={smallBtn}>{notStarted ? 'Change start date…' : 'Change finish date…'}</SecondaryButton>
        )}
        {!(ahead && item.can_accept) && (
          <SecondaryButton disabled={busy} onClick={onNotNow} style={smallBtn}>Not now</SecondaryButton>
        )}
      </span>
    </div>
  );
}

/**
 * The banner: every task on this job whose sub's progress waits on the PM, in the schedule's order. Nothing when there
 * are none. `livePct(taskId)`: the task's % in the schedule now. `onChanged` after an answer (reload what waits);
 * `onTaskChanged` after a % is set (reload the schedule); `onOpenTask(taskId)` for its dates.
 */
export default function SubProgressBanner({ items, codeOf, livePct, focused, onChanged, onTaskChanged, onOpenTask }) {
  const [busy, setBusy] = useState(null); // a task id, or 'all'
  const [error, setError] = useState(null);
  const { confirm, confirmDialogProps } = useConfirm();
  const shown = (items || []).map((x) => withLive(x, livePct ? livePct(x.task_id) : null)).filter(Boolean);
  if (!shown.length) return null;
  const acceptable = shown.filter((x) => x.can_accept);

  const setPct = (x) => updateTask(x.task_id, { percent_complete: x.target_pct, status: statusOf(x.target_pct) });
  const accept = async (x) => {
    setBusy(x.task_id); setError(null);
    try {
      await setPct(x);
      onTaskChanged();
      onChanged();
    } catch (e) {
      setError(`${x.task_code || x.task_name}: ${e.message || 'Couldn’t set its %.'}`);
    } finally {
      setBusy(null);
    }
  };
  const acceptAll = async () => {
    const ok = await confirm({
      title: `Accept the subs’ progress on ${acceptable.length} tasks?`,
      message: (
        <>
          {acceptable.slice(0, 8).map((x) => (
            <div key={x.key}><span className="mono" style={{ fontSize: 13 }}>{x.task_code}</span> {x.task_name}: {x.schedule_pct}% → {x.target_pct}%</div>
          ))}
          {acceptable.length > 8 && <div>and {acceptable.length - 8} more</div>}
          <div style={{ marginTop: 8 }}>Each is set the way your own edit does, so SOV and billing follow.</div>
        </>
      ),
      confirmLabel: 'Accept all',
      danger: false,
    });
    if (!ok) return;
    setBusy('all'); setError(null);
    const failed = [];
    for (const x of acceptable) {
      try { await setPct(x); } catch (e) { failed.push(`${x.task_code || x.task_name}: ${e.message || 'Couldn’t set its %.'}`); }
    }
    setBusy(null);
    onTaskChanged();
    onChanged();
    if (failed.length) setError(failed.join(' · '));
  };
  const notNow = async (x) => {
    setBusy(x.task_id); setError(null);
    try {
      await dismissNotification({ alert_key: x.key, action: 'read' });
      onChanged();
    } catch (e) {
      setError(e.message || 'Couldn’t put it off.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div id="sub-progress" style={{
      marginBottom: 14, borderRadius: 12, overflow: 'hidden', background: 'var(--bg-surface)',
      border: '1px solid color-mix(in oklch, var(--warning) 35%, transparent)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '9px 14px', background: 'var(--warning-soft)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 14.5, fontWeight: 600, color: 'var(--warning)', flex: '1 1 auto' }}>
          <Icon name="trending-up" size={15} />
          Sub progress waiting on you · {shown.length} task{shown.length === 1 ? '' : 's'}
        </span>
        {acceptable.length > 1 && (
          <PrimaryButton busy={busy === 'all'} disabled={!!busy && busy !== 'all'} onClick={acceptAll} style={smallBtn}>Accept all</PrimaryButton>
        )}
      </div>
      {shown.map((x) => (
        <ProgressRow key={x.key} item={x} code={codeOf ? codeOf(x.task_id) : null} focused={focused === x.task_id}
          busy={busy === x.task_id || busy === 'all'} onAccept={() => accept(x)} onNotNow={() => notNow(x)}
          onOpenTask={() => onOpenTask(x.task_id)} />
      ))}
      {error && <div style={{ padding: '0 14px 10px' }}><ErrorBanner>{error}</ErrorBanner></div>}
      <div style={{ padding: '8px 14px', borderTop: '1px solid var(--border-subtle)', fontSize: 12.5, color: 'var(--text-3)' }}>
        Accepting sets the task’s % the way your own edit does, so SOV and billing follow. The bell shows these on the job too.
      </div>
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}
